<template>
  <RootPageLayout
    class="discover-page"
    title="Discover the world's best ideas"
    mobileTitle="Discover"
  >
    <div
      v-show="!wait.is('Loading Discover')"
      class="layout__wrapper-inner layout__wrapper-inner--notifications container"
    >
      <Checklist
        v-if="
          checklist() &&
          !checklist()?.isCompleted &&
          !wait.is('Loading Discover')
        "
        :docs="getChecklistBooks()"
      />
      <EnableNotificationsCTA />
    </div>

    <v-wait for="Error Loading Discover">
      <template v-slot:waiting>
        <LoadingError />
      </template>
    </v-wait>

    <v-wait class="layout__wrapper-inner container" for="Loading Discover">
      <template v-slot:waiting>
        <loading-spinner />
      </template>

      <ContinueReadingBooksBlock
        v-if="!showNewLibrary20321 && continueReadingView"
        :view="continueReadingView"
      />

      <MultiDocsBlock20321
        v-if="
          showNewLibrary20321 &&
          continueReading20321View &&
          continueReading20321View.dataLength
        "
        :view="continueReading20321View"
        showFreeBooks
      />

      <WantToReadBooksBlock
        v-if="wantToReadView && wantToReadView.dataLength > 0"
        :view="wantToReadView"
      />

      <FreeBooksBlock
        v-if="freeBooksView && freeBooksView.dataLength && isFreeUser"
        :view="freeBooksView"
      />
      <ExcerptBlock v-if="excerptView" :view="excerptView" />
      <BooksBlock
        v-if="recommendedBooksView && hasRecommendations"
        :view="recommendedBooksView"
      />
      <NewReleasesBooksBlock v-if="newReleasesView" :view="newReleasesView" />
      <BooksBlock v-if="popularBooksView" :view="popularBooksView" />
      <CollectionsBlock v-if="collectionsView" :view="collectionsView" />

      <BookOfTheDayBlock
        v-if="bookOfTheDayView"
        :view="bookOfTheDayView"
        class="book-day-box"
        title="Book of the Day"
      />

      <MultiDocsBlock20321
        v-if="showNewLibrary20321 && articlesView?.dataLength"
        :view="articlesView"
      />
      <ArticlesBlock v-else-if="articlesView" :view="articlesView" />
    </v-wait>
  </RootPageLayout>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeMount, onMounted, provide, ref } from 'vue'
import type { Raw } from 'vue'
import { useRouter } from 'vue-router'
import { useWait } from '@/app/composables/useWait'
import { Sentry } from '@/services/sentry'

import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import RootPageLayout from '@/app/components/RootPageLayout.vue'
import { backend } from '@/services/backend'
import {
  BookOfTheDayView,
  BooksView,
  showReviewPrompt,
  trustpilotReviewPrompt,
} from '@/models/doc.book'
import { ArticlesView } from '@/models/doc.article'
import { onboardingChecklist19055Experiment } from '@/services/ab'

import ContinueReadingBooksBlock from '@/app/components/listing/ContinueReadingBooksBlock.vue'
import WantToReadBooksBlock from '@/app/components/listing/WantToReadBooksBlock.vue'
import NewReleasesBooksBlock from '@/app/components/listing/NewReleasesBooksBlock.vue'
import FreeBooksBlock from '@/app/components/listing/FreeBooksBlock.vue'
import BooksBlock from '@/app/components/listing/BooksBlock.vue'
import ArticlesBlock from '@/app/components/listing/ArticlesBlock.vue'
import {
  createDocsView,
  DocSortOption,
  DocsView,
  DocsViewMediator,
} from '@/models/doc'
import {
  continueReading19038Variant,
  saveForLater20321Treatment,
} from '@/services/ab'
import { initializeFirstLaunchList } from '@/models/onboarding'
import { auth } from '@/services/auth'
import { CollectionsView } from '@/models/docs.list.collection'
import CollectionsBlock from '@/app/components/collections/CollectionsBlock.vue'
import { rotateRandomly } from '@/helpers/list'
import BookOfTheDayBlock from '@/app/components/listing/BookOfTheDayBlock.vue'
import { ExcerptView } from '@/models/excerpt'
import ExcerptBlock from '@/app/components/excerpt/ExcerptBlock.vue'
import { ListView } from '@/models/docs.list'
import MultiDocsBlock20321 from '@/app/components/listing/MultiDocsBlock20321.vue'
import type {
  BookListItem,
  Doc,
  DocListItem,
  DocsUserDataResponse,
  ListItem,
} from '@/models/interfaces'
import EnableNotificationsCTA from '@/app/components/EnableNotificationsCTA.vue'
import { checklist } from '@/models/checklist'
import Checklist from '@/app/components/Checklist.vue'
import { ChecklistDocs } from '@/models/checklist.types'
import { Mixpanel } from '@/analytics/mixpanel'

const router = useRouter()
const wait = useWait()

const articlesView = ref<Raw<ListView> | null>(null)
const freeBooksView = ref<Raw<ListView> | null>(null)
const newReleasesView = ref<Raw<ListView> | null>(null)
const continueReadingView = ref<Raw<ListView> | null>(null)
const continueReading20321View = ref<Raw<ListView> | null>(null)
const wantToReadView = ref<Raw<ListView> | null>(null)
const popularBooksView = ref<Raw<ListView> | null>(null)
const recommendedBooksView = ref<Raw<ListView> | null>(null)
const collectionsView = ref<Raw<ListView> | null>(null)
const bookOfTheDayView = ref<Raw<BookOfTheDayView> | null>(null)
const excerptView = ref<ExcerptView | null>(null)

const isFreeUser = ref(false)
const failedBlocks = ref(0)
const listingBlocks: Record<string, string[]> = {}

// Let nested ListingBlocks register their docs for the "Discover Page
// Loaded" Mixpanel event. `provide()` allows this without passing a callback
// through each of their parent block components.
provide('registerDiscoverListingBlock', registerListingBlock)

onBeforeMount(async () => {
  isFreeUser.value = auth.isFreePlan()

  wait.start('Loading Discover')

  // This is one of 2 possible places where we can start this experiment.
  // The other place is after starting a trial. We start the experiment here
  // if the user didn't start a trial before visiting the Discover page.
  if (auth.isFreePlan()) {
    await onboardingChecklist19055Experiment('free')
  }

  await getBooksAndArticles()
})

onMounted(async () => {
  const urlQueryParams = router.currentRoute.value.query

  // Check if we need to show the App Review Prompt
  // The trigger is going back to Home from reading a book. (#2812)
  if (urlQueryParams.from === 'book.page') {
    await showReviewPrompt()
    await trustpilotReviewPrompt()
  }
})

async function getBooksAndArticles(): Promise<void> {
  wait.start('Loading Discover')

  // Finish list initialization before loading blocks so a newly created list
  // is included in the first response.
  await initializeFirstLaunchList()
  const userDocsPromise = backend.getUserDocs()
  const docsUserDataPromise = backend.getDocsUserData()
  const blocksSetupFunctions = [
    setupBooks,
    setupArticles,
    setupBookCollections,
    setupExcerpt,
    setupWantToRead,
  ]
  if (showNewLibrary20321.value) {
    blocksSetupFunctions.push(setupNowReading20321)
  }

  const setupPromises = blocksSetupFunctions.map((setupFunction) =>
    setupFunction(userDocsPromise, docsUserDataPromise),
  )
  const setupResults = await Promise.allSettled(setupPromises)
  setupResults.forEach((result) => {
    if (result.status === 'rejected') {
      failedBlocks.value++
    }
  })

  // Adding each view to the mediator allows them to update each other: e.g. a
  // doc in one list added to another shows up in that other list.
  new DocsViewMediator(
    [
      continueReadingView,
      continueReading20321View,
      wantToReadView,
      popularBooksView,
      recommendedBooksView,
      newReleasesView,
      freeBooksView,
      articlesView,
    ]
      .map((listView) => listView.value?.docs())
      .filter((view) => view instanceof DocsView),
  )

  wait.end('Loading Discover')

  if (failedBlocks.value >= blocksSetupFunctions.length) {
    // If all blocks fail, display an error
    wait.start('Error Loading Discover')
  } else {
    // Wait for the listing blocks inside v-wait to mount and register.
    await nextTick()
    Mixpanel.trackDiscoverPageLoaded(listingBlocks)
  }

  // Load Book of the Day.
  // This loads independently after the page loads (non-blocking).
  setupBookOfTheDay()
}

/**
 * Add a rendered listing block to the Discover page analytics data.
 */
function registerListingBlock(title: string, docTitles: string[]): void {
  listingBlocks[title] = docTitles
}

/**
 * Load the Want to Read row.
 */
async function setupWantToRead(
  userDocsPromise: Promise<Doc[]>,
  docsUserDataPromise: Promise<DocsUserDataResponse>,
): Promise<void> {
  const [response, docsUserData] = await Promise.all([
    backend.getCustomListDocs('want-to-read'),
    docsUserDataPromise,
  ])

  // Both variants will be displayed by WantToReadBooksBlock.

  if (!showNewLibrary20321.value) {
    const booksView = await BooksView.createView(
      response,
      docsUserData,
      DocSortOption.none, // The response is already sorted.
      false, // Default showCtaBlock value.
      false, // Default showFreeAtTheTop value.
    )
    await booksView.setCustomList(response.list.id)
    booksView.enableActionsButton()

    wantToReadView.value = new ListView('Want to Read', booksView, {
      viewAllLink: '/app/list/want-to-read',
      customList: response.list,
    })
  } else {
    // 20321-style multi-type doc list.
    const wantToReadList = await createDocsView(
      response.data,
      docsUserData,
      DocSortOption.viewed,
    )
    await wantToReadList.setCustomList(response.list.id)
    wantToReadList.enableActionsButton()

    wantToReadView.value = new ListView('Want to Read', wantToReadList, {
      customCardComponent: 'AnyDocCard',
      viewAllLink: `/app/library/list/want-to-read`,
      customList: response.list,
    })
  }
}

async function setupBooks(
  userDocsPromise: Promise<Doc[]>,
  docsUserDataPromise: Promise<DocsUserDataResponse>,
): Promise<void> {
  const [booksData, userDocs, ownedIds, docsUserData] = await Promise.all([
    backend.getBooksWithRecommendations(),
    userDocsPromise,
    backend.getOwnedBookIds(),
    docsUserDataPromise,
  ])

  if (booksData) {
    const showCtaBlock = false
    const showFreeAtTheTop = false

    let unownedBooksData: BookListItem[] = booksData.data.filter(
      (doc) => !ownedIds.has(doc.id),
    )

    const reading = await BooksView.createView(
      {
        data: userDocs.filter((doc) => doc.doc_type === 'book'),
      },
      docsUserData,
      undefined, // The defaultSort
      showCtaBlock,
      showFreeAtTheTop,
    )
    reading.enableContinueReading()
    reading.enableActionsButton()

    // In variant B Reading Now is using History list and shows only
    // the books still in progress, finished books stay reachable through
    // the "View all" link.(Continue Reading Foundations #19038)
    const isContinueReading19038 = continueReading19038Variant() === 'B'
    await reading.setList(isContinueReading19038 ? 'history' : 'reading')
    await reading.setOnlyInProgress(isContinueReading19038)
    await reading.sort(
      isContinueReading19038 ? DocSortOption.viewed : DocSortOption.accessed,
    )
    continueReadingView.value = new ListView('Reading Now', reading, {
      viewAllLink: isContinueReading19038
        ? '/app/books/list/history'
        : '/app/books/list/reading',
      customCardComponent: 'ContinueReadingCard',
    })

    const free = await BooksView.createView(
      booksData,
      docsUserData,
      DocSortOption.first_published_at,
      showCtaBlock,
      showFreeAtTheTop,
    )
    free.filterByFree()
    freeBooksView.value = new ListView('Free Books', free, {
      customCardComponent: 'NewReleasesCard',
    })

    const newReleases = await BooksView.createView(
      { data: unownedBooksData },
      docsUserData,
      DocSortOption.first_published_at,
      showCtaBlock,
      showFreeAtTheTop,
    )
    newReleasesView.value = new ListView('New Releases', newReleases, {
      viewAllLink: '/app/books/list/new',
      customCardComponent: 'NewReleasesCard',
    })

    const popularBooks = await BooksView.createView(
      { data: unownedBooksData },
      docsUserData,
      DocSortOption.popular,
      showCtaBlock,
      showFreeAtTheTop,
    )
    popularBooksView.value = new ListView('Popular Books', popularBooks, {
      viewAllLink: '/app/books/list/popular',
    })

    const recommendedBooks = await BooksView.createView(
      { data: unownedBooksData },
      docsUserData,
      DocSortOption.recommended,
      false, // Default showCtaBlock value
      false, // Default showFreeAtTheTop value
    )
    recommendedBooksView.value = new ListView('Recommended', recommendedBooks, {
      viewAllLink: '/app/books/list/recommended',
    })
  }
}

async function setupNowReading20321(
  userDocsPromise: Promise<Doc[]>,
  docsUserDataPromise: Promise<DocsUserDataResponse>,
): Promise<void> {
  const [docsData, docsUserData] = await Promise.all([
    userDocsPromise,
    docsUserDataPromise,
  ])

  // This is the same as the Reading Now block in LibraryHome20321.

  const readingNow = await createDocsView(
    docsData,
    docsUserData,
    DocSortOption.viewed,
  )
  await readingNow.setList('reading')
  await readingNow.setOnlyInProgress(false)
  readingNow.enableContinueReading()
  readingNow.enableActionsButton()
  continueReading20321View.value = new ListView('Reading Now', readingNow, {
    customCardComponent: 'AnyDocCard',
    viewAllLink: '/app/library/reading',
  })
}

async function setupArticles(
  userDocsPromise: Promise<Doc[]>,
  docsUserDataPromise: Promise<DocsUserDataResponse>,
): Promise<void> {
  const articlesData = await backend.getHomeArticles()
  if (articlesData) {
    const articles = await ArticlesView.createView(
      articlesData,
      undefined,
      false,
    )
    articlesView.value = new ListView('Articles', articles, {
      customCardComponent: showNewLibrary20321.value ? 'AnyDocCard' : undefined,
      viewAllLink: '/app/articles/list/all',
    })
    articlesView.value.setUserData(docsUserDataPromise)
  }
}

async function setupBookCollections(): Promise<void> {
  const collectionsData = await backend.getBookCollections()
  if (collectionsData) {
    const collections = new CollectionsView(rotateRandomly(collectionsData))
    collectionsView.value = new ListView('Collections', collections, {
      viewAllLink: '/app/collections',
    })
  }
}

async function setupExcerpt(): Promise<void> {
  const excerptData = await backend.getExcerpt()
  if (excerptData) {
    excerptView.value = new ExcerptView(excerptData)
  }
}

async function setupBookOfTheDay(): Promise<void> {
  try {
    const bookOfTheDayData = await backend.getBookOfTheDay()
    if (bookOfTheDayData) {
      bookOfTheDayView.value =
        await BookOfTheDayView.createView(bookOfTheDayData)
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}

const hasRecommendations = computed(() => {
  const view = recommendedBooksView.value?.docs() as BooksView | undefined
  return !!view?.hasRecommendations
})

const showNewLibrary20321 = computed(() => {
  return saveForLater20321Treatment()
})

/**
 * Get the list of books for the Checklist recommendations.
 */
function getChecklistBooks(): ChecklistDocs {
  // This is an object, rather than an array,
  // so that we can ensure that all books are unique.
  const selectedBooks: Record<string, ListItem> = {}

  // This view is filtered to only books with `list === 'reading'`, but the `.books` property
  // retains a copy of the original books before filtering (ie. all user docs from `/home/docs`).
  const userBooks = (continueReadingView.value?.docs() as BooksView)?.books

  const wantToReadBooks =
    userBooks?.filter((doc) =>
      // Docs that are in the `want-to-read` list.
      doc.user?.custom_lists.find((list) => list.url_slug === 'want-to-read'),
    ) || []
  const favoritedBooks = userBooks?.filter((doc) => doc.user?.is_favorite) || []
  const recommendedBooks = recommendedBooksView.value?.docs().filteredDocs || []
  const popularBooks = popularBooksView.value?.docs().filteredDocs || []
  const freeBooks = freeBooksView.value?.docs().filteredDocs || []

  // This set will do _some_ de-duplication of the books, but it might still
  // have some duplicates, because different views can have separate references
  // to the same book.
  const allBooks = new Set([
    ...wantToReadBooks,
    ...favoritedBooks,
    ...recommendedBooks,
    ...popularBooks,
    ...freeBooks,
  ])

  // Select only free books for free users, and all books for paid users.
  if (isFreeUser.value) {
    allBooks.forEach((doc) => {
      if ((doc as DocListItem).is_free) {
        selectedBooks[doc.id] = doc
      }
    })
  } else {
    allBooks.forEach((doc) => {
      selectedBooks[doc.id] = doc
    })
  }

  return {
    reading: continueReadingView.value?.docs().filteredDocs[0],
    recommendations: Object.values(selectedBooks).slice(0, 3),
  }
}
</script>
